/** Wiki entry history page — revision list with diff links. */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../auth/api';
import { Clock, GitCompare, Eye } from 'lucide-react';

export default function WikiHistory() {
    const { id } = useParams();
    const [revisions, setRevisions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get(`/wiki/entry/${id}/history`);
                setRevisions(res.data || []);
            } catch (e) { setError('Failed to load history'); }
            setLoading(false);
        };
        load();
    }, [id]);

    const fmtDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center gap-3 mb-6">
                <Link to="/wiki" className="text-slate-400 hover:text-white text-sm">&laquo; Wiki Home</Link>
                <span className="text-slate-600">|</span>
                <h1 className="text-lg font-bold text-white">Revision History</h1>
            </div>

            {error && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

            {loading ? (
                <div className="text-slate-400 text-sm">Loading...</div>
            ) : revisions.length === 0 ? (
                <div className="text-slate-400 text-sm">No revisions yet.</div>
            ) : (
                <div className="space-y-3">
                    {revisions.map((rev, i) => (
                        <div key={rev.revision_num || i} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                                    <Clock size={14} className="text-slate-400" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-white">
                                        {rev.revision_num ? `Revision ${rev.revision_num}` : `#${i + 1}`}
                                    </div>
                                    <div className="text-xs text-slate-400">
                                        by {rev.author_name || 'Unknown'} &middot; {fmtDate(rev.created_at)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Link
                                    to={`/wiki/entry/${id}?rev=${rev.revision_num}`}
                                    className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-xs text-slate-300"
                                >
                                    <Eye size={12} /> View
                                </Link>
                                {i > 0 && (
                                    <Link
                                        to={`/wiki/entry/${id}/diff?from=${revisions[i - 1].revision_num}&to=${rev.revision_num}`}
                                        className="flex items-center gap-1 px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-xs text-amber-400"
                                    >
                                        <GitCompare size={12} /> Diff
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}